import { useState, useEffect } from "react";
import { Task, TaskStatus, TaskView, TaskFilter } from "@/types/task-management";
import TaskListView from "./TaskListView";
import TaskKanbanView from "./TaskKanbanView";
import TaskListItem from "./TaskListItem";
import { mockTaskViews } from "@/lib/mock/task-management";
import TaskService from "@/services/taskService";
import CreateTaskDialog from "./CreateTaskDialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  ChevronDown, 
  LayoutGrid, 
  List, 
  Save,
  Sun,
  Loader2
} from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useAuthContext } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { v4 as uuidv4 } from "uuid";
import confetti from 'canvas-confetti';
import { DragDropContext, DropResult } from 'react-beautiful-dnd';

interface TaskManagerProps {
  initialView?: "list" | "kanban";
  savedViews?: TaskView[];
}

export default function TaskManager({ 
  initialView = "list",
  savedViews = mockTaskViews,
}: TaskManagerProps) {
  // Estado das tarefas
  const [tasks, setTasks] = useState<Task[]>([]);
  const [activeView, setActiveView] = useState<"list" | "kanban">(initialView);
  const [views, setViews] = useState<TaskView[]>(savedViews);
  const [currentViewName, setCurrentViewName] = useState<string>("Todas as tarefas");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [activeFilter, setActiveFilter] = useState<TaskFilter>({});
  
  const { toast } = useToast();
  const { user } = useAuthContext();
  
  // Função para disparar o efeito confetti
  const triggerConfetti = () => {
    confetti({
      particleCount: 150,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#4CAF50', '#8BC34A', '#CDDC39', '#FFC107', '#FF9800']
    });
  };
  
  // Verificar e criar usuário na tabela users se necessário
  const ensureUserExists = async (userId: string, userEmail: string) => {
    try {
      // Verificar se o usuário já existe na tabela users
      const { data, error } = await supabase
        .from('users')
        .select('id')
        .eq('id', userId)
        .single();
      
      if (error || !data) {
        // Usuário não existe, criar um novo registro
        const now = new Date().toISOString();
        const { error: insertError } = await supabase
          .from('users')
          .insert({
            id: userId,
            email: userEmail,
            role: 'user',
            is_active: true,
            created_at: now,
            updated_at: now
          });
        
        if (insertError) {
          console.error('Erro ao criar usuário na tabela users:', insertError);
          return false;
        }
        
        console.log('Usuário criado na tabela users com sucesso!');
        return true;
      }
      
      return true; // Usuário já existe
    } catch (error) {
      console.error('Erro ao verificar/criar usuário:', error);
      return false;
    }
  };
  
  // Carregar tarefas do Supabase ao montar o componente
  useEffect(() => {
    fetchTasks();
  }, [activeFilter]);
  
  // Função para buscar tarefas
  const fetchTasks = async () => {
    try {
      setIsLoading(true);
      const result = await TaskService.getTasks(activeFilter);
      setTasks(result.tasks);
    } catch (error) {
      console.error("Erro ao carregar tarefas:", error);
      toast({
        title: "Erro ao carregar tarefas",
        description: "Não foi possível obter as tarefas. Por favor, tente novamente.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Manipuladores de eventos para tarefas
  const handleCreateTask = async (taskData: Partial<Task>) => {
    try {
      if (!user) {
        toast({
          title: "Erro ao criar tarefa",
          description: "É necessário estar autenticado para criar uma tarefa.",
          variant: "destructive"
        });
        return;
      }
      
      setIsLoading(true);
      
      // Garantir que o usuário existe na tabela users antes de criar a tarefa
      const userExists = await ensureUserExists(user.id, user.email || '');
      
      if (!userExists) {
        toast({
          title: "Erro ao criar tarefa",
          description: "Não foi possível verificar seu usuário no sistema. Por favor, tente novamente.",
          variant: "destructive"
        });
        setIsLoading(false);
        return;
      }
      
      const newTask = await TaskService.createTask(taskData, user.id);
      setTasks([...tasks, newTask]);
      
      toast({
        title: "Tarefa criada",
        description: "A tarefa foi criada com sucesso.",
      });
    } catch (error) {
      console.error("Erro ao criar tarefa:", error);
      toast({
        title: "Erro ao criar tarefa",
        description: "Não foi possível criar a tarefa. Por favor, tente novamente.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleUpdateTask = async (taskId: string, updates: Partial<Task>) => {
    try {
      if (!user) {
        toast({
          title: "Erro ao atualizar tarefa",
          description: "É necessário estar autenticado para atualizar uma tarefa.",
          variant: "destructive"
        });
        return;
      }
      
      setIsLoading(true);
      
      // Garantir que o usuário existe na tabela users antes de atualizar a tarefa
      const userExists = await ensureUserExists(user.id, user.email || '');
      
      if (!userExists) {
        toast({
          title: "Erro ao atualizar tarefa",
          description: "Não foi possível verificar seu usuário no sistema. Por favor, tente novamente.",
          variant: "destructive"
        });
        setIsLoading(false);
        return;
      }
      
      const updatedTask = await TaskService.updateTask(taskId, updates, user.id);
      
      // Atualizar o estado local
      setTasks(tasks.map(task => 
        task.id === taskId ? updatedTask : task
      ));
      
      toast({
        title: "Tarefa atualizada",
        description: "As alterações foram salvas com sucesso.",
      });
    } catch (error) {
      console.error("Erro ao atualizar tarefa:", error);
      toast({
        title: "Erro ao atualizar tarefa",
        description: "Não foi possível salvar as alterações. Por favor, tente novamente.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleStatusChange = async (taskId: string, status: TaskStatus) => {
    try {
      if (!user) {
        toast({
          title: "Erro ao alterar status",
          description: "É necessário estar autenticado para alterar o status de uma tarefa.",
          variant: "destructive"
        });
        return;
      }
      
      setIsLoading(true);
      
      // Garantir que o usuário existe na tabela users antes de alterar o status
      const userExists = await ensureUserExists(user.id, user.email || '');
      
      if (!userExists) {
        toast({
          title: "Erro ao alterar status",
          description: "Não foi possível verificar seu usuário no sistema. Por favor, tente novamente.",
          variant: "destructive"
        });
        setIsLoading(false);
        return;
      }
      
      await TaskService.changeTaskStatus(taskId, status, user.id);
      
      // Atualizar o estado local
      setTasks(tasks.map(task => 
        task.id === taskId 
          ? { ...task, status, updated_at: new Date().toISOString() } 
          : task
      ));

      // Disparar confetti quando a tarefa for concluída
      if (status === 'completed') {
        triggerConfetti();
        toast({
          title: "🎉 Tarefa concluída!",
          description: "Parabéns por completar esta tarefa!",
        });
      }
    } catch (error) {
      console.error("Erro ao alterar status da tarefa:", error);
      toast({
        title: "Erro ao alterar status",
        description: "Não foi possível atualizar o status da tarefa.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Lidar com o início do drag and drop
  const handleDragStart = (result: DropResult) => {
    // Add drag indication to body for styling
    document.body.classList.add('dragging-task');
  };
  
  // Lidar com o resultado do drag and drop
  const handleDragEnd = async (result: DropResult) => {
    // Remove drag indication from body
    document.body.classList.remove('dragging-task');
    
    const { source, destination, draggableId } = result;
    
    // Se não houver destino ou o item for solto na mesma posição, não fazer nada
    if (!destination || 
        (source.droppableId === destination.droppableId && 
          source.index === destination.index)) {
      return;
    }
    
    // O destination.droppableId contém o status de destino
    const newStatus = destination.droppableId as TaskStatus;
    
    // Atualizar o status da tarefa usando a função handleStatusChange existente
    await handleStatusChange(draggableId, newStatus);
    
    // Verificar se a tarefa está sendo movida para a coluna de concluídos
    const taskToMove = tasks.find(task => task.id === draggableId);
    if (taskToMove && taskToMove.status !== "completed" && newStatus === "completed") {
      // Disparar efeito de confetti após um pequeno atraso para a animação de transição terminar
      setTimeout(() => {
        triggerConfetti();
      }, 300);
    }
  };
  
  const handleAddToMyDay = async (taskId: string, added: boolean) => {
    try {
      if (!user) {
        toast({
          title: "Erro ao atualizar Meu Dia",
          description: "É necessário estar autenticado para gerenciar suas tarefas diárias.",
          variant: "destructive"
        });
        return;
      }
      
      setIsLoading(true);
      
      // Garantir que o usuário existe na tabela users antes de atualizar Meu Dia
      const userExists = await ensureUserExists(user.id, user.email || '');
      
      if (!userExists) {
        toast({
          title: "Erro ao atualizar Meu Dia",
          description: "Não foi possível verificar seu usuário no sistema. Por favor, tente novamente.",
          variant: "destructive"
        });
        setIsLoading(false);
        return;
      }
      
      await TaskService.toggleMyDay(taskId, added, user.id);
      
      // Atualizar o estado local
      setTasks(tasks.map(task => 
        task.id === taskId 
          ? { ...task, added_to_my_day: added, updated_at: new Date().toISOString() } 
          : task
      ));
      
      toast({
        title: added ? "Adicionado ao Meu Dia" : "Removido do Meu Dia",
        description: added 
          ? "A tarefa foi adicionada ao seu planejamento diário." 
          : "A tarefa foi removida do seu planejamento diário.",
      });
    } catch (error) {
      console.error("Erro ao atualizar Meu Dia:", error);
      toast({
        title: "Erro ao atualizar Meu Dia",
        description: "Não foi possível atualizar o status Meu Dia da tarefa.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDeleteTask = async (taskId: string) => {
    try {
      if (!user) {
        toast({
          title: "Erro ao excluir tarefa",
          description: "É necessário estar autenticado para excluir uma tarefa.",
          variant: "destructive"
        });
        return;
      }
      
      setIsLoading(true);
      
      // Garantir que o usuário existe na tabela users antes de excluir a tarefa
      const userExists = await ensureUserExists(user.id, user.email || '');
      
      if (!userExists) {
        toast({
          title: "Erro ao excluir tarefa",
          description: "Não foi possível verificar seu usuário no sistema. Por favor, tente novamente.",
          variant: "destructive"
        });
        setIsLoading(false);
        return;
      }
      
      await TaskService.deleteTask(taskId, user.id);
      
      // Remover a tarefa do estado local
      setTasks(tasks.filter(task => task.id !== taskId));
      
      toast({
        title: "Tarefa excluída",
        description: "A tarefa foi excluída com sucesso.",
      });
    } catch (error) {
      console.error("Erro ao excluir tarefa:", error);
      toast({
        title: "Erro ao excluir tarefa",
        description: "Não foi possível excluir a tarefa. Por favor, tente novamente.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Prorrogar tarefa para o próximo dia útil
  const handlePostponeTask = async (taskId: string) => {
    try {
      if (!user) {
        toast({
          title: "Erro ao prorrogar tarefa",
          description: "É necessário estar autenticado para prorrogar uma tarefa.",
          variant: "destructive"
        });
        return;
      }
      
      setIsLoading(true);
      
      // Garantir que o usuário existe na tabela users antes de prorrogar a tarefa
      const userExists = await ensureUserExists(user.id, user.email || '');
      
      if (!userExists) {
        toast({
          title: "Erro ao prorrogar tarefa",
          description: "Não foi possível verificar seu usuário no sistema. Por favor, tente novamente.",
          variant: "destructive"
        });
        setIsLoading(false);
        return;
      }
      
      // Encontrar a tarefa e obter sua data atual
      const task = tasks.find(t => t.id === taskId);
      if (!task) {
        throw new Error("Tarefa não encontrada");
      }
      
      // Calcular a nova data (próximo dia útil)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      let nextDate = new Date(today);
      nextDate.setDate(today.getDate() + 1);
      
      // Se cair no fim de semana, avançar para segunda-feira
      const dayOfWeek = nextDate.getDay();
      if (dayOfWeek === 0) { // Domingo
        nextDate.setDate(nextDate.getDate() + 1);
      } else if (dayOfWeek === 6) { // Sábado
        nextDate.setDate(nextDate.getDate() + 2);
      }
      
      // Atualizar a tarefa com a nova data
      await TaskService.updateTask(taskId, {
        due_date: nextDate.toISOString()
      }, user.id);
      
      // Atualizar o estado local
      setTasks(tasks.map(task => 
        task.id === taskId 
          ? { ...task, due_date: nextDate.toISOString(), updated_at: new Date().toISOString() } 
          : task
      ));
      
      toast({
        title: "Tarefa prorrogada",
        description: "A data de vencimento foi adiada para o próximo dia útil.",
      });
    } catch (error) {
      console.error("Erro ao prorrogar tarefa:", error);
      toast({
        title: "Erro ao prorrogar tarefa",
        description: "Não foi possível prorrogar a tarefa. Por favor, tente novamente.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Filtrar tarefas por playbook
  const handleFilterByPlaybook = (playbookId: string | null) => {
    if (playbookId) {
      setActiveFilter({...activeFilter, playbook_id: playbookId});
    } else {
      const newFilter = {...activeFilter};
      delete newFilter.playbook_id;
      setActiveFilter(newFilter);
    }
  };
  
  // Filtrar tarefas para "Meu dia"
  const toggleMyDayFilter = () => {
    setActiveFilter({
      ...activeFilter,
      is_personal: !activeFilter.is_personal
    });
  };
  
  // Salvar visualização atual
  const saveCurrentView = (viewName: string) => {
    // Implementação simplificada - em um app real, isso salvaria no backend
    const newView: TaskView = {
      id: uuidv4(),
      name: viewName,
      type: activeView,
      filters: activeFilter,
      sort: { field: "due_date", direction: "asc" },
    };
    
    setViews([...views, newView]);
    setCurrentViewName(viewName);
    
    toast({
      title: "Visualização salva",
      description: `A visualização "${viewName}" foi salva com sucesso.`,
    });
  };
  
  // Carregar visualização salva
  const loadView = (view: TaskView) => {
    setActiveView(view.type);
    setCurrentViewName(view.name);
    setActiveFilter(view.filters || {});
  };
  
  // Filtrar tarefas para "Meu dia"
  const myDayTasks = tasks.filter(task => task.added_to_my_day && task.status !== 'completed');
  const hasMyDayTasks = myDayTasks.length > 0;
  
  return (
    <div className="space-y-4">
      {/* Vista de lista ou kanban */}
      <div className="w-full">
        {activeView === "list" && (
          <TaskListView
            tasks={tasks}
            onTaskCreate={handleCreateTask}
            onTaskUpdate={handleUpdateTask}
            onTaskStatusChange={handleStatusChange}
            onAddToMyDay={handleAddToMyDay}
            onDeleteTask={handleDeleteTask}
            onPostponeTask={handlePostponeTask}
            isLoading={isLoading}
          />
        )}
        
        {activeView === "kanban" && (
          <DragDropContext onDragEnd={handleDragEnd}>
            <TaskKanbanView
              tasks={tasks}
              onTaskCreate={handleCreateTask}
              onTaskUpdate={handleUpdateTask}
              onTaskStatusChange={handleStatusChange}
              onAddToMyDay={handleAddToMyDay}
              onDeleteTask={handleDeleteTask}
              onPostponeTask={handlePostponeTask}
              isLoading={isLoading}
              onFilterByPlaybook={handleFilterByPlaybook}
            />
          </DragDropContext>
        )}
      </div>
    </div>
  );
} 